import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { GroupBuyProviderSchema } from "./commercial.js";
import {
  type GroupBuyDailyReconciliationOutcome,
  GroupBuyDailyReconciliationOutcomeSchema,
} from "./group-buy-daily-reconciliation.js";
import {
  GroupBuyReconciliationNumberSchema,
  isGroupBuyReconciliationNumber,
} from "./group-buy-reconciliation-number.js";

const StableIdSchema = Type.String({
  minLength: 12,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$",
});
export const GroupBuyReconciliationInternalReferenceIdSchema = Type.String({
  minLength: 12,
  maxLength: 512,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{11,511}$",
});
export function isGroupBuyReconciliationInternalReferenceId(value: unknown): value is string {
  return Value.Check(GroupBuyReconciliationInternalReferenceIdSchema, value);
}
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const SignedAmountFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 2, maxLength: maximum, pattern: CanonicalTextPattern });
const PlatformReferenceSchema = Type.String({
  minLength: 4,
  maxLength: 64,
  pattern: "^[^\\s]+$",
});
const ExplanationListSchema = Type.Array(CanonicalTextSchema(300), {
  minItems: 1,
  maxItems: 8,
  uniqueItems: true,
});

export const GroupBuyReconciliationResolutionDispositionSchema = Type.Union([
  Type.Literal("PLATFORM_RECEIVABLE"),
  Type.Literal("ACCEPT_PLATFORM_ADJUSTMENT"),
  Type.Literal("EXCLUDE_DUPLICATE_OR_UNRELATED"),
]);
export type GroupBuyReconciliationResolutionDisposition = Static<
  typeof GroupBuyReconciliationResolutionDispositionSchema
>;

export const GroupBuyReconciliationResolutionRequestLineSchema = Type.Object(
  {
    targetId: GroupBuyReconciliationInternalReferenceIdSchema,
    disposition: GroupBuyReconciliationResolutionDispositionSchema,
    evidenceReference: CanonicalTextSchema(120),
    note: CanonicalTextSchema(500),
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionRequestLine = Static<
  typeof GroupBuyReconciliationResolutionRequestLineSchema
>;

const ResolutionRequestFields = {
  resolutionId: StableIdSchema,
  reconciliationId: StableIdSchema,
  reviewWorkItemId: GroupBuyReconciliationInternalReferenceIdSchema,
  lines: Type.Array(GroupBuyReconciliationResolutionRequestLineSchema, {
    minItems: 1,
    maxItems: 1_000,
  }),
};

export const PreviewGroupBuyReconciliationResolutionRequestSchema = Type.Object(
  ResolutionRequestFields,
  { additionalProperties: false },
);
export type PreviewGroupBuyReconciliationResolutionRequest = Static<
  typeof PreviewGroupBuyReconciliationResolutionRequestSchema
>;

const ResolutionTargetCommonFields = {
  targetId: GroupBuyReconciliationInternalReferenceIdSchema,
  platformReference: PlatformReferenceSchema,
  expectedSettlementFen: AmountFenSchema,
  expectedFeeFen: AmountFenSchema,
  expectedSubsidyFen: AmountFenSchema,
  actualSettlementFen: AmountFenSchema,
  actualFeeFen: AmountFenSchema,
  actualSubsidyFen: AmountFenSchema,
  expectedNetSettlementFen: SignedAmountFenSchema,
  actualNetSettlementFen: SignedAmountFenSchema,
  differenceFen: SignedAmountFenSchema,
};

export const GroupBuyReconciliationResolutionTargetSchema = Type.Union([
  Type.Object(
    {
      ...ResolutionTargetCommonFields,
      kind: Type.Literal("FULFILLMENT"),
      fulfillmentId: StableIdSchema,
      tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
      packageName: Type.String({ minLength: 1, maxLength: 80 }),
      originalOutcome: GroupBuyDailyReconciliationOutcomeSchema,
      statementLineNumbers: Type.Array(Type.Integer({ minimum: 1, maximum: 500 }), {
        maxItems: 500,
        uniqueItems: true,
      }),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ResolutionTargetCommonFields,
      kind: Type.Literal("STATEMENT_LINE"),
      statementLineNumber: Type.Integer({ minimum: 1, maximum: 500 }),
      originalOutcome: Type.Literal("STATEMENT_ONLY"),
    },
    { additionalProperties: false },
  ),
]);
export type GroupBuyReconciliationResolutionTarget = Static<
  typeof GroupBuyReconciliationResolutionTargetSchema
>;

export const GroupBuyReconciliationResolutionSourceReadModelSchema = Type.Object(
  {
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    reviewWorkItemId: GroupBuyReconciliationInternalReferenceIdSchema,
    businessDate: BusinessDateSchema,
    provider: GroupBuyProviderSchema,
    statementReference: CanonicalTextSchema(80),
    targets: Type.Array(GroupBuyReconciliationResolutionTargetSchema, {
      minItems: 1,
      maxItems: 1_000,
    }),
    exceptionCount: Type.Integer({ minimum: 1, maximum: 1_000 }),
    responsibleRole: Type.Literal("MANAGER"),
    copiedRoles: Type.Tuple([Type.Literal("OWNER")]),
    escalationStatus: Type.Literal("OWNER_NOTIFIED"),
    escalatedAt: TimestampSchema,
    status: Type.Literal("PENDING_REVIEW"),
    completedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionSourceReadModel = Static<
  typeof GroupBuyReconciliationResolutionSourceReadModelSchema
>;

export const GroupBuyReconciliationResolutionSourceListReadModelSchema = Type.Object(
  {
    items: Type.Array(GroupBuyReconciliationResolutionSourceReadModelSchema, { maxItems: 100 }),
    pendingCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionSourceListReadModel = Static<
  typeof GroupBuyReconciliationResolutionSourceListReadModelSchema
>;

export const GroupBuyReconciliationResolutionLineSchema = Type.Object(
  {
    target: GroupBuyReconciliationResolutionTargetSchema,
    disposition: GroupBuyReconciliationResolutionDispositionSchema,
    evidenceReference: CanonicalTextSchema(120),
    note: CanonicalTextSchema(500),
    ledgerEntryType: Type.Union([
      Type.Literal("PLATFORM_RECEIVABLE"),
      Type.Literal("SETTLEMENT_ADJUSTMENT"),
      Type.Null(),
    ]),
    ledgerAmountFen: Type.Union([SignedAmountFenSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionLine = Static<
  typeof GroupBuyReconciliationResolutionLineSchema
>;

export const GroupBuyReconciliationResolutionPreviewSchema = Type.Object(
  {
    kind: Type.Literal("GROUP_BUY_RECONCILIATION_RESOLUTION"),
    resolutionId: StableIdSchema,
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    reviewWorkItemId: GroupBuyReconciliationInternalReferenceIdSchema,
    businessDate: BusinessDateSchema,
    provider: GroupBuyProviderSchema,
    statementReference: CanonicalTextSchema(80),
    lines: Type.Array(GroupBuyReconciliationResolutionLineSchema, {
      minItems: 1,
      maxItems: 1_000,
    }),
    exceptionCount: Type.Integer({ minimum: 1, maximum: 1_000 }),
    platformReceivableFen: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    settlementAdjustmentFen: SignedAmountFenSchema,
    excludedNetSettlementFen: SignedAmountFenSchema,
    sourceReconciliationSha256: Sha256Schema,
    originalDocumentLabel: CanonicalTextSchema(120),
    consequences: ExplanationListSchema,
    nextSteps: ExplanationListSchema,
    requirement: Type.Literal("MANAGER_OR_OWNER"),
    approverLabel: CanonicalTextSchema(120),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionPreview = Static<
  typeof GroupBuyReconciliationResolutionPreviewSchema
>;

export const CreateGroupBuyReconciliationResolutionApprovalCommandSchema = Type.Object(
  {
    ...ResolutionRequestFields,
    sourceReconciliationSha256: Sha256Schema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: Type.String({ minLength: 8, maxLength: 128 }),
  },
  { additionalProperties: false },
);
export type CreateGroupBuyReconciliationResolutionApprovalCommand = Static<
  typeof CreateGroupBuyReconciliationResolutionApprovalCommandSchema
>;

export const GroupBuyReconciliationResolutionApprovalSnapshotSchema = Type.Object(
  {
    ...GroupBuyReconciliationResolutionPreviewSchema.properties,
    snapshotVersion: Type.Literal(1),
    operationTrace: BusinessOperationTraceV1Schema,
    commandSha256: Sha256Schema,
    capturedAt: TimestampSchema,
    orderEffect: Type.Literal("NONE"),
    inventoryEffect: Type.Literal("NONE"),
    memberBalanceEffect: Type.Literal("NONE"),
    originalBusinessFactEffect: Type.Literal("NONE"),
    moneyLedgerEffect: Type.Literal("APPEND_ONLY_GROUP_BUY_SETTLEMENT"),
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationResolutionApprovalSnapshot = Static<
  typeof GroupBuyReconciliationResolutionApprovalSnapshotSchema
>;

export const GroupBuySettlementLedgerEntrySchema = Type.Object(
  {
    entryId: StableIdSchema,
    resolutionId: StableIdSchema,
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    targetId: GroupBuyReconciliationInternalReferenceIdSchema,
    entryType: Type.Union([
      Type.Literal("PLATFORM_RECEIVABLE"),
      Type.Literal("SETTLEMENT_ADJUSTMENT"),
    ]),
    amountFen: SignedAmountFenSchema,
    evidenceReference: CanonicalTextSchema(120),
    note: CanonicalTextSchema(500),
    occurredAt: TimestampSchema,
    applicantActorId: StableIdSchema,
    authorizerActorId: StableIdSchema,
    executorActorId: StableIdSchema,
  },
  { additionalProperties: false },
);
export type GroupBuySettlementLedgerEntry = Static<typeof GroupBuySettlementLedgerEntrySchema>;

export const GroupBuyReconciliationSignoffPackageSchema = Type.Object(
  {
    resolutionId: StableIdSchema,
    reconciliationId: StableIdSchema,
    reconciliationNumber: GroupBuyReconciliationNumberSchema,
    approvalId: StableIdSchema,
    snapshot: GroupBuyReconciliationResolutionApprovalSnapshotSchema,
    ledgerEntries: Type.Array(GroupBuySettlementLedgerEntrySchema, { maxItems: 1_000 }),
    reviewWorkItemStatus: Type.Literal("RESOLVED"),
    applicantActorId: StableIdSchema,
    authorizerActorId: StableIdSchema,
    executorActorId: StableIdSchema,
    status: Type.Literal("SIGNED_OFF"),
    signedOffAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationSignoffPackage = Static<
  typeof GroupBuyReconciliationSignoffPackageSchema
>;

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function safeAdd(left: number, right: number): number | null {
  const result = left + right;
  return Number.isSafeInteger(result) ? result : null;
}

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    const next = safeAdd(total, value);
    if (next === null) return null;
    total = next;
  }
  return total;
}

export function groupBuyNetSettlementFen(input: {
  settlementFen: number;
  feeFen: number;
  subsidyFen: number;
}): number | null {
  if (
    !Number.isSafeInteger(input.settlementFen) ||
    !Number.isSafeInteger(input.feeFen) ||
    !Number.isSafeInteger(input.subsidyFen) ||
    input.settlementFen < 0 ||
    input.feeFen < 0 ||
    input.subsidyFen < 0
  ) {
    return null;
  }
  return safeAdd(input.settlementFen - input.feeFen, input.subsidyFen);
}

function requestIsSemanticallyValid(
  value: PreviewGroupBuyReconciliationResolutionRequest,
): boolean {
  return (
    value.lines.length > 0 &&
    new Set(value.lines.map((line) => line.targetId)).size === value.lines.length
  );
}

function targetIsSemanticallyValid(target: GroupBuyReconciliationResolutionTarget): boolean {
  const expectedNet = groupBuyNetSettlementFen({
    settlementFen: target.expectedSettlementFen,
    feeFen: target.expectedFeeFen,
    subsidyFen: target.expectedSubsidyFen,
  });
  const actualNet = groupBuyNetSettlementFen({
    settlementFen: target.actualSettlementFen,
    feeFen: target.actualFeeFen,
    subsidyFen: target.actualSubsidyFen,
  });
  if (expectedNet === null || actualNet === null) return false;
  if (
    target.expectedNetSettlementFen !== expectedNet ||
    target.actualNetSettlementFen !== actualNet ||
    target.differenceFen !== actualNet - expectedNet
  ) {
    return false;
  }
  if (target.kind === "STATEMENT_LINE") {
    return (
      target.expectedSettlementFen === 0 &&
      target.expectedFeeFen === 0 &&
      target.expectedSubsidyFen === 0
    );
  }
  return target.originalOutcome !== "CONFIRMED";
}

function resolutionLineIsSemanticallyValid(line: GroupBuyReconciliationResolutionLine): boolean {
  if (!targetIsSemanticallyValid(line.target)) return false;
  switch (line.disposition) {
    case "PLATFORM_RECEIVABLE":
      return (
        line.target.differenceFen < 0 &&
        line.ledgerEntryType === "PLATFORM_RECEIVABLE" &&
        line.ledgerAmountFen === -line.target.differenceFen
      );
    case "ACCEPT_PLATFORM_ADJUSTMENT":
      return (
        line.target.differenceFen !== 0 &&
        line.ledgerEntryType === "SETTLEMENT_ADJUSTMENT" &&
        line.ledgerAmountFen === line.target.differenceFen
      );
    case "EXCLUDE_DUPLICATE_OR_UNRELATED":
      return (
        (line.target.kind === "STATEMENT_LINE" ||
          line.target.originalOutcome === "DUPLICATE_RECORD") &&
        line.ledgerEntryType === null &&
        line.ledgerAmountFen === null
      );
  }
}

function previewIsSemanticallyValid(preview: GroupBuyReconciliationResolutionPreview): boolean {
  const targetIds = preview.lines.map((line) => line.target.targetId);
  const receivable = safeSum(
    preview.lines
      .filter((line) => line.ledgerEntryType === "PLATFORM_RECEIVABLE")
      .map((line) => line.ledgerAmountFen ?? 0),
  );
  const adjustment = safeSum(
    preview.lines
      .filter((line) => line.ledgerEntryType === "SETTLEMENT_ADJUSTMENT")
      .map((line) => line.ledgerAmountFen ?? 0),
  );
  const excluded = safeSum(
    preview.lines
      .filter((line) => line.ledgerEntryType === null)
      .map((line) => line.target.actualNetSettlementFen),
  );
  return (
    isCanonicalBusinessDate(preview.businessDate) &&
    isCanonicalTimestamp(preview.generatedAt) &&
    isGroupBuyReconciliationNumber(preview.reconciliationNumber) &&
    targetIds.length === preview.exceptionCount &&
    new Set(targetIds).size === targetIds.length &&
    preview.lines.every(resolutionLineIsSemanticallyValid) &&
    receivable === preview.platformReceivableFen &&
    adjustment === preview.settlementAdjustmentFen &&
    excluded === preview.excludedNetSettlementFen &&
    preview.canSubmit &&
    preview.blocker === null
  );
}

export function isPreviewGroupBuyReconciliationResolutionRequest(
  value: unknown,
): value is PreviewGroupBuyReconciliationResolutionRequest {
  return (
    Value.Check(PreviewGroupBuyReconciliationResolutionRequestSchema, value) &&
    requestIsSemanticallyValid(value as PreviewGroupBuyReconciliationResolutionRequest)
  );
}

export function isGroupBuyReconciliationResolutionTarget(
  value: unknown,
): value is GroupBuyReconciliationResolutionTarget {
  return (
    Value.Check(GroupBuyReconciliationResolutionTargetSchema, value) &&
    targetIsSemanticallyValid(value as GroupBuyReconciliationResolutionTarget)
  );
}

export function isGroupBuyReconciliationResolutionSourceReadModel(
  value: unknown,
): value is GroupBuyReconciliationResolutionSourceReadModel {
  if (!Value.Check(GroupBuyReconciliationResolutionSourceReadModelSchema, value)) return false;
  const source = value as GroupBuyReconciliationResolutionSourceReadModel;
  const targetIds = source.targets.map((target) => target.targetId);
  return (
    isCanonicalBusinessDate(source.businessDate) &&
    isCanonicalTimestamp(source.completedAt) &&
    isCanonicalTimestamp(source.escalatedAt) &&
    Date.parse(source.escalatedAt) >= Date.parse(source.completedAt) &&
    isGroupBuyReconciliationNumber(source.reconciliationNumber) &&
    source.targets.length === source.exceptionCount &&
    new Set(targetIds).size === targetIds.length &&
    source.targets.every(targetIsSemanticallyValid)
  );
}

export function isGroupBuyReconciliationResolutionSourceListReadModel(
  value: unknown,
): value is GroupBuyReconciliationResolutionSourceListReadModel {
  if (!Value.Check(GroupBuyReconciliationResolutionSourceListReadModelSchema, value)) return false;
  const list = value as GroupBuyReconciliationResolutionSourceListReadModel;
  const reconciliationIds = list.items.map((item) => item.reconciliationId);
  return (
    isCanonicalTimestamp(list.generatedAt) &&
    list.pendingCount === list.items.length &&
    new Set(reconciliationIds).size === reconciliationIds.length &&
    list.items.every(isGroupBuyReconciliationResolutionSourceReadModel)
  );
}

export function isGroupBuyReconciliationResolutionPreview(
  value: unknown,
): value is GroupBuyReconciliationResolutionPreview {
  return (
    Value.Check(GroupBuyReconciliationResolutionPreviewSchema, value) &&
    previewIsSemanticallyValid(value as GroupBuyReconciliationResolutionPreview)
  );
}

export function isCreateGroupBuyReconciliationResolutionApprovalCommand(
  value: unknown,
): value is CreateGroupBuyReconciliationResolutionApprovalCommand {
  if (!Value.Check(CreateGroupBuyReconciliationResolutionApprovalCommandSchema, value))
    return false;
  const command = value as CreateGroupBuyReconciliationResolutionApprovalCommand;
  return (
    requestIsSemanticallyValid(command) &&
    isBusinessOperationTraceV1(command.operationTrace) &&
    command.operationTrace.idempotencyKey === command.idempotencyKey &&
    command.operationTrace.correlationId ===
      `group-buy-reconciliation:${command.reconciliationId}` &&
    command.operationTrace.businessNumber?.kind === "GROUP_BUY_RECONCILIATION" &&
    isGroupBuyReconciliationNumber(command.operationTrace.businessNumber.value)
  );
}

export function isGroupBuyReconciliationResolutionApprovalSnapshot(
  value: unknown,
): value is GroupBuyReconciliationResolutionApprovalSnapshot {
  if (!Value.Check(GroupBuyReconciliationResolutionApprovalSnapshotSchema, value)) return false;
  const snapshot = value as GroupBuyReconciliationResolutionApprovalSnapshot;
  return (
    previewIsSemanticallyValid(snapshot) &&
    isCanonicalTimestamp(snapshot.capturedAt) &&
    Date.parse(snapshot.capturedAt) >= Date.parse(snapshot.generatedAt) &&
    isBusinessOperationTraceV1(snapshot.operationTrace) &&
    snapshot.operationTrace.correlationId ===
      `group-buy-reconciliation:${snapshot.reconciliationId}` &&
    snapshot.operationTrace.businessNumber?.kind === "GROUP_BUY_RECONCILIATION" &&
    snapshot.operationTrace.businessNumber.value === snapshot.reconciliationNumber
  );
}

export function isGroupBuySettlementLedgerEntry(
  value: unknown,
): value is GroupBuySettlementLedgerEntry {
  if (!Value.Check(GroupBuySettlementLedgerEntrySchema, value)) return false;
  const entry = value as GroupBuySettlementLedgerEntry;
  return (
    isCanonicalTimestamp(entry.occurredAt) &&
    isGroupBuyReconciliationNumber(entry.reconciliationNumber) &&
    entry.amountFen !== 0 &&
    (entry.entryType !== "PLATFORM_RECEIVABLE" || entry.amountFen > 0) &&
    entry.applicantActorId !== entry.authorizerActorId
  );
}

export function isGroupBuyReconciliationSignoffPackage(
  value: unknown,
): value is GroupBuyReconciliationSignoffPackage {
  if (!Value.Check(GroupBuyReconciliationSignoffPackageSchema, value)) return false;
  const record = value as GroupBuyReconciliationSignoffPackage;
  const expectedEntries = record.snapshot.lines.filter((line) => line.ledgerEntryType !== null);
  return (
    isGroupBuyReconciliationResolutionApprovalSnapshot(record.snapshot) &&
    isCanonicalTimestamp(record.signedOffAt) &&
    record.resolutionId === record.snapshot.resolutionId &&
    record.reconciliationId === record.snapshot.reconciliationId &&
    record.reconciliationNumber === record.snapshot.reconciliationNumber &&
    record.ledgerEntries.length === expectedEntries.length &&
    record.ledgerEntries.every(isGroupBuySettlementLedgerEntry) &&
    new Set(record.ledgerEntries.map((entry) => entry.entryId)).size ===
      record.ledgerEntries.length &&
    new Set(record.ledgerEntries.map((entry) => entry.targetId)).size ===
      record.ledgerEntries.length &&
    record.ledgerEntries.every((entry) => {
      const line = expectedEntries.find(
        (candidate) => candidate.target.targetId === entry.targetId,
      );
      return (
        line !== undefined &&
        entry.resolutionId === record.resolutionId &&
        entry.reconciliationId === record.reconciliationId &&
        entry.reconciliationNumber === record.reconciliationNumber &&
        entry.entryType === line.ledgerEntryType &&
        entry.amountFen === line.ledgerAmountFen &&
        entry.evidenceReference === line.evidenceReference &&
        entry.note === line.note &&
        entry.applicantActorId === record.applicantActorId &&
        entry.authorizerActorId === record.authorizerActorId &&
        entry.executorActorId === record.executorActorId &&
        entry.occurredAt === record.signedOffAt
      );
    }) &&
    record.applicantActorId !== record.authorizerActorId &&
    Date.parse(record.signedOffAt) >= Date.parse(record.snapshot.capturedAt)
  );
}

export function groupBuyReconciliationResolutionTargetId(input: {
  reconciliationId: string;
  kind: "FULFILLMENT" | "STATEMENT_LINE";
  sourceId: string | number;
}): string {
  return `group-buy-resolution:${input.reconciliationId}:${input.kind.toLowerCase()}:${String(input.sourceId)}`;
}

export function groupBuyResolutionDispositionIsAllowed(
  disposition: GroupBuyReconciliationResolutionDisposition,
  target: GroupBuyReconciliationResolutionTarget,
): boolean {
  switch (disposition) {
    case "PLATFORM_RECEIVABLE":
      return target.differenceFen < 0;
    case "ACCEPT_PLATFORM_ADJUSTMENT":
      return target.differenceFen !== 0;
    case "EXCLUDE_DUPLICATE_OR_UNRELATED":
      return target.kind === "STATEMENT_LINE" || target.originalOutcome === "DUPLICATE_RECORD";
  }
}

export function groupBuyResolutionLedgerEffect(input: {
  disposition: GroupBuyReconciliationResolutionDisposition;
  target: GroupBuyReconciliationResolutionTarget;
}): Pick<GroupBuyReconciliationResolutionLine, "ledgerEntryType" | "ledgerAmountFen"> | null {
  if (!groupBuyResolutionDispositionIsAllowed(input.disposition, input.target)) return null;
  switch (input.disposition) {
    case "PLATFORM_RECEIVABLE":
      return {
        ledgerEntryType: "PLATFORM_RECEIVABLE",
        ledgerAmountFen: -input.target.differenceFen,
      };
    case "ACCEPT_PLATFORM_ADJUSTMENT":
      return {
        ledgerEntryType: "SETTLEMENT_ADJUSTMENT",
        ledgerAmountFen: input.target.differenceFen,
      };
    case "EXCLUDE_DUPLICATE_OR_UNRELATED":
      return { ledgerEntryType: null, ledgerAmountFen: null };
  }
}

export function groupBuyResolutionTargetOutcome(
  target: GroupBuyReconciliationResolutionTarget,
): GroupBuyDailyReconciliationOutcome | "STATEMENT_ONLY" {
  return target.originalOutcome;
}
